/* eslint-disable no-nested-ternary */
import { forwardRef, useState, memo, useRef } from "react";
import { useField } from "formik";
import PropTypes from "prop-types";
import { Upload, Form, message, Button, Progress } from "antd";
import { LoadingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import UploadIcon from "assets/images/upload.svg";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import Helper from "utils/helpers";
import ImgCrop from "antd-img-crop";
import "../style.less";

const UploadImage = forwardRef(({ label, className = "", aspectRatio = 4/3, cropModalProps = {}, disableCrop = false, ...props }, ref) => {
  const [field, meta, helper] = useField(props);
  const uploadRef = useRef(null);
  const isError = meta.error && meta.touched;
  const [isLoading, setIsloading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const image = !_isEmpty(field.value)
    ? [{ uid: field.value.objectId || 'temp', url: _get(field.value, "url") || field.value }]
    : [];

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return false;
    }
    return true;
  };

  const handleRemove = () => {
    helper.setValue(undefined);
  };

  const handleChange = (img) => {
    helper.setValue({ ...img, url: img.url });
  };

  const customRequest = async (option) => {
    const { file } = option;
    setIsloading(true);
    setUploadProgress(0);
    
    try {
      const img = await Helper.uploadFile(
        { file, type: "AVATAR" },
        (progress) => {
          setUploadProgress(progress);
        }
      );
      
      if (img) {
        handleChange(img);
      } else {
        message.error("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Upload failed. Please try again.");
    } finally {
      setIsloading(false);
      setUploadProgress(0);
    }
  };

  const renderUploadContent = () => (
    <div ref={uploadRef}>
      {isLoading ? (
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined />
          {uploadProgress > 0 && (
            <Progress 
              percent={uploadProgress} 
              size="small" 
              style={{ marginTop: 8 }}
            />
          )}
        </div>
      ) : !_isEmpty(image) ? (
        image.map((img) => (
          <img
            className="uploaded-image"
            key={img.uid}
            alt=""
            src={img.url}
          />
        ))
      ) : (
        <img src={UploadIcon} alt="" />
      )}
    </div>
  );

  return (
    <div className={`input-container upload-image-container ${className}`}>
      <Form.Item
        label={label}
        validateStatus={isError ? "error" : ""}
        help={
          isError && (
            <div className="helper-wrapper">
              <div className="error-text">
                <InfoCircleOutlined
                  className="info-icon"
                  type="info-circle"
                />
                {meta.error}
              </div>
            </div>
          )
        }
      >
        {disableCrop ? (
          <Upload
            listType="picture-card"
            fileList={image}
            showUploadList={false}
            beforeUpload={beforeUpload}
            onRemove={handleRemove}
            customRequest={customRequest}
          >
            {renderUploadContent()}
          </Upload>
        ) : (
          <ImgCrop 
            rotate 
            aspect={aspectRatio}
            modalProps={{
              width: 600,
              centered: true,
              ...cropModalProps
            }}
          >
            <Upload
              listType="picture-card"
              fileList={image}
              showUploadList={false}
              beforeUpload={beforeUpload}
              onRemove={handleRemove}
              customRequest={customRequest}
            >
              {renderUploadContent()}
            </Upload>
          </ImgCrop>
        )}

        <Button
          size="small"
          type="primary"
          style={{ width: 100 }}
          onClick={() => uploadRef.current?.click()}
        >
          Chọn hình
        </Button>
      </Form.Item>
    </div>
  );
});

UploadImage.displayName = "UploadImage";
UploadImage.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf([
    "AVATAR",
    "COVER_IMAGE",
    "POST_IMAGE",
    "COMPANY_AVATAR",
    "COMPANY_STORE_AVATAR",
  ]),
  className: PropTypes.string,
  aspectRatio: PropTypes.number,
  cropModalProps: PropTypes.object,
  disableCrop: PropTypes.bool,
};

export default memo(UploadImage);
